import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag, Share2, Clock, User, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import axios from 'axios';

const API = '/api';

// Category images from Unsplash - Car related photos
const categoryImages = {
  financing: [
    'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=1200&q=80',
    'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1200&q=80',
    'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200&q=80'
  ],
  credit: [
    'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1200&q=80',
    'https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=1200&q=80',
    'https://images.unsplash.com/photo-1542362567-b07e54358753?w=1200&q=80'
  ],
  industry: [
    'https://images.unsplash.com/photo-1485291571150-772bcfc10da5?w=1200&q=80',
    'https://images.unsplash.com/photo-1493238792000-8113da705763?w=1200&q=80',
    'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=1200&q=80'
  ],
  guides: [
    'https://images.unsplash.com/photo-1502877338535-766e1452684a?w=1200&q=80',
    'https://images.unsplash.com/photo-1489824904134-891ab64532f1?w=1200&q=80',
    'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=1200&q=80'
  ]
};

const BlogPost = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  const categories = {
    financing: { en: 'Auto Financing', fr: 'Financement auto', color: 'bg-blue-500' },
    credit: { en: 'Credit Tips', fr: 'Conseils crédit', color: 'bg-purple-500' },
    industry: { en: 'Industry News', fr: 'Actualités', color: 'bg-orange-500' },
    guides: { en: 'Buying Guides', fr: "Guides d'achat", color: 'bg-green-500' }
  };

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
      
      const allPosts = await axios.get(`${API}/blog/posts`);
      const related = allPosts.data
        .filter(p => p.slug !== slug)
        .slice(0, 3);
      setRelatedPosts(related);
    } catch (error) {
      console.error('Error fetching blog post:', error);
    }
    setLoading(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-CA' : 'fr-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getImageForCategory = (category) => {
    const images = categoryImages[category] || categoryImages.financing;
    return images[Math.floor(Math.random() * images.length)];
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-[#1a5c3b] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            {language === 'en' ? 'Article not found' : 'Article non trouvé'}
          </h1>
          <Link to="/blog">
            <Button className="bg-[#1a5c3b]">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'en' ? 'Back to Blog' : 'Retour au blogue'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const title = language === 'en' ? post.title_en : post.title_fr;
  const content = language === 'en' ? post.content_en : post.content_fr;
  const heroImage = post.image_url || getImageForCategory(post.category);
  const categoryInfo = categories[post.category] || categories.financing;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center text-sm text-gray-500">
            <Link to="/" className="hover:text-[#1a5c3b]">{language === 'en' ? 'Home' : 'Accueil'}</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/blog" className="hover:text-[#1a5c3b]">{language === 'en' ? 'Blog' : 'Blogue'}</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 truncate max-w-xs">{title}</span>
          </div>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative h-[400px] md:h-[500px] overflow-hidden">
        <img 
          src={heroImage} 
          alt={title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="container mx-auto max-w-4xl">
            <Badge className={`${categoryInfo.color} text-white mb-4`}>
              <Tag className="w-3 h-3 mr-1" />
              {categoryInfo[language === 'en' ? 'en' : 'fr']}
            </Badge>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                {language === 'en' ? '5 min read' : '5 min de lecture'}
              </span>
              <span className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                Credit Auto Reset
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              <Card className="bg-white p-6 md:p-10 shadow-lg rounded-2xl">
                <div 
                  className="prose prose-lg max-w-none 
                    prose-headings:text-gray-900 prose-headings:font-bold
                    prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-2
                    prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                    prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                    prose-a:text-[#1a5c3b] prose-a:font-semibold
                    prose-strong:text-gray-900
                    prose-ul:my-4 prose-li:text-gray-700
                    prose-img:rounded-xl prose-img:shadow-lg"
                  dangerouslySetInnerHTML={{ __html: content }}
                />

                {/* Inline CTA */}
                <div className="mt-10 p-6 bg-gradient-to-r from-[#1a5c3b] to-[#165432] rounded-2xl text-white">
                  <h3 className="text-xl font-bold mb-2">
                    {language === 'en' ? 'Need Auto Financing?' : 'Besoin de financement auto?'}
                  </h3>
                  <p className="text-green-100 mb-4">
                    {language === 'en' 
                      ? 'We specialize in bad credit and second chance financing. Get approved today!'
                      : 'Nous sommes spécialisés dans le mauvais crédit et le financement 2e chance. Soyez approuvé aujourd\'hui!'}
                  </p>
                  <Link to="/demander-un-pret-auto">
                    <Button className="bg-white text-[#1a5c3b] hover:bg-gray-100">
                      {language === 'en' ? 'Apply Now - Free!' : 'Faire une demande - Gratuit!'}
                    </Button>
                  </Link>
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Share */}
                <Card className="bg-white p-5 shadow-lg rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <Share2 className="w-4 h-4 mr-2 text-[#1a5c3b]" />
                    {language === 'en' ? 'Share' : 'Partager'}
                  </h3>
                  <div className="flex gap-2">
                    <a 
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center"
                    >
                      <span className="text-xs">Facebook</span>
                    </a>
                    <a 
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 p-3 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors text-center"
                    >
                      <span className="text-xs">Twitter</span>
                    </a>
                  </div>
                </Card>

                {/* Quick Contact */}
                <Card className="bg-[#1a5c3b] p-5 shadow-lg rounded-xl text-white">
                  <h3 className="font-bold mb-2">
                    {language === 'en' ? 'Questions?' : 'Questions?'}
                  </h3>
                  <p className="text-green-100 text-sm mb-4">
                    {language === 'en' ? 'Call us now!' : 'Appelez-nous!'}
                  </p>
                  <a href="tel:514-418-3968" className="text-2xl font-bold hover:underline">
                    514-418-3968
                  </a>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-white py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              {language === 'en' ? 'Related Articles' : 'Articles similaires'}
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {relatedPosts.map((relatedPost) => {
                const relatedImage = relatedPost.image_url || getImageForCategory(relatedPost.category);
                return (
                  <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`}>
                    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 h-full">
                      <div className="h-40 overflow-hidden">
                        <img 
                          src={relatedImage} 
                          alt={language === 'en' ? relatedPost.title_en : relatedPost.title_fr}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-4">
                        <Badge className={`${categories[relatedPost.category]?.color || 'bg-gray-500'} text-white text-xs mb-2`}>
                          {categories[relatedPost.category]?.[language === 'en' ? 'en' : 'fr'] || relatedPost.category}
                        </Badge>
                        <h3 className="font-bold text-gray-900 line-clamp-2">
                          {language === 'en' ? relatedPost.title_en : relatedPost.title_fr}
                        </h3>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="bg-gradient-to-r from-[#1a5c3b] to-[#165432] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {language === 'en' ? 'Ready to Get Approved?' : 'Prêt à être approuvé?'}
          </h2>
          <p className="text-green-100 mb-8 max-w-2xl mx-auto">
            {language === 'en' 
              ? 'Bad credit? No problem! We specialize in helping Quebecers get the car they need.'
              : 'Mauvais crédit? Pas de problème! Nous aidons les Québécois à obtenir la voiture dont ils ont besoin.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demander-un-pret-auto">
              <Button size="lg" className="bg-white text-[#1a5c3b] hover:bg-gray-100 px-8">
                {language === 'en' ? 'Apply Now' : 'Faire une demande'}
              </Button>
            </Link>
            <a href="tel:514-418-3968">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#1a5c3b] px-8">
                514-418-3968
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
